import { ConfirmDialog } from "@/design-system/dialogs/ConfirmDialog";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, X } from "lucide-react";

import { updateMember } from "@/api/members";
import type { MemberPayload, MemberResponse } from "@/api/members";
import { assignMemberToHousehold, removeMemberFromHousehold } from "@/api/households";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { MemberForm } from "@/features/members/components/MemberForm";

import { refreshHouseholdQueries, householdError } from "@/features/households/management";

type EditMemberDialogProps = {
  member: MemberResponse;
  initialOpen?: boolean;
  onClose?: () => void;
  hideTrigger?: boolean;
};

export function EditMemberDialog({ member, initialOpen = false, onClose, hideTrigger = false }: EditMemberDialogProps) {
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();
  const queryClient = useQueryClient();
  const saving = useRef(false);
  const [removalPayload, setRemovalPayload] = useState<MemberPayload | null>(null);
  const [householdPending, setHouseholdPending] = useState(false);
  const [isOpen, setIsOpen] = useState(initialOpen);
  const close = () => { setRemovalPayload(null); setIsOpen(false); onClose?.(); };

  const updateMutation = useMutation({
    mutationFn: async (payload: MemberPayload) => {
      const {
        household_id,
        household_name,
        relationship_to_household,
        is_head_of_household,
        ...memberPayload
      } = payload;

      void household_name;

      const updatedMember = await updateMember(
        churchId!,
        member.id,
        memberPayload
      );

      if (household_id !== undefined && (household_id || member.household_id)) {
        try {
          if (household_id) await assignMemberToHousehold(churchId!, household_id, {
            member_id: member.id,
            relationship_to_household,
            is_head_of_household: Boolean(is_head_of_household),
          });
          else await removeMemberFromHousehold(churchId!, member.household_id!, member.id);
        } catch (error) {
          void refreshHouseholdQueries(queryClient, churchId!);
          throw new Error(`Member details were saved, but household assignment failed. ${householdError(error)}`, { cause: error });
        }
      }

      return updatedMember;
    },
    onSuccess: async () => {
      await refreshHouseholdQueries(queryClient, churchId!);
      close();
    },
    onSettled: () => { saving.current = false; },
  });

  if (!permissions.has("MEMBERS_MANAGE")) return null;

  return (
    <>
      {!hideTrigger && <button
        type="button"
        onClick={() => { updateMutation.reset(); setIsOpen(true); }}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800"
      >
        <Pencil className="h-4 w-4" />
        Edit
      </button>}

      <Modal open={isOpen} onClose={() => { if (!updateMutation.isPending && !householdPending) { if (removalPayload) setRemovalPayload(null); else close(); } }} ariaLabelledBy="edit-member-dialog-title" panelClassName="max-w-4xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 id="edit-member-dialog-title" className="text-xl font-semibold text-white">
                  Edit Member
                </h2>
                <p className="text-sm text-slate-400">
                  Update member profile and household information.
                </p>
              </div>

              <button
                type="button"
                disabled={updateMutation.isPending || householdPending}
                onClick={close}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
                aria-label="Close edit member dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div hidden={!!removalPayload} inert={!!removalPayload}>
            <MemberForm
              member={member}
              submitLabel="Save Changes"
              isSubmitting={updateMutation.isPending}
              onHouseholdPendingChange={setHouseholdPending}
              onSubmit={(payload) => {
                if (saving.current) return;
                if (payload.household_id === null && member.household_id) { setRemovalPayload(payload); return; }
                saving.current = true;
                updateMutation.mutate(payload);
              }}
            />

            </div>
            <ConfirmDialog inline open={!!removalPayload} title={`Remove ${member.first_name} ${member.last_name} from their household?`}
              description={`Only the household association will be removed. The member and attendance, care, ministry and other history remain.${member.is_head_of_household ? " This member is the head of household. Removing them will leave this household without a designated head." : ""}`}
              destructive confirmText="Remove from Household" pending={updateMutation.isPending}
              onCancel={() => { if (!saving.current) setRemovalPayload(null); }}
              onConfirm={() => { if (!saving.current && removalPayload) { saving.current = true; updateMutation.mutate(removalPayload); } }} />
            {updateMutation.isError && (
              <p className="mt-4 text-sm text-red-400">
                {householdError(updateMutation.error)}
              </p>
            )}
      </Modal>
    </>
  );
}